import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../shared/state/root-reducer";
import { formatNumberToUSD } from "@finpay-development/shared-platform/lib/esm/shared/calculators";
import { Typography, Box, Grid, FormControlLabel, Checkbox, IconButton } from "@mui/material";
import { admissionsAdvisorUtils } from "@finpay-development/shared-platform/lib/esm/admissions-advisor/utils/admission-advisor-utils";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AppDispatch } from "../../../shared/state/store";
import { CrossoverSelectionEnum, SummarySelections } from "@finpay-estimation/estimation-types";
import { Utils } from "@finpay-development/shared-platform/lib/esm/shared/utils";
import { useIsDisabledAfterConvert } from "../../hooks/use-is-disabled-after-convert";
import CrossoverSummaryModal from "./modals/crossover-summary-modal";
import EstFinancialSummaryModal from "./modals/financial-summary-modal";
import { showErrorStatus } from "@finpay-development/shared-platform/lib/esm/security/state/user-slice";
import _ from 'lodash';
import {saveSelectedEstimate} from "@finpay-development/shared-platform/lib/esm/shared/state/estimator-thunk";

export function SidebarTotalEstimatedPfr() {

  const state = {
    vobState: useSelector(
      (state: RootState) => state.vobContext.selectedVob
    ),
    estimate: useSelector((state: RootState) => {
      return state.estimatorContext.selectedEstimate
    }),
    rateAsPayorPlan: useSelector(
      (state: RootState) => state.estimatorContext.rateAsPayorPlan
    )
  };

	const {
		vobState,
    estimate,
    rateAsPayorPlan
	} = state;

	const [financialSummaryModalOpen, setFinancialSummaryModalOpen] = useState(false);
	const [totalEstimatedPfrSelected, setTotalEstimatedPfrSelected] = useState(false);
  const [isCrossoverSummaryModalOpen, setIsCrossoverSummaryModalOpen] = useState(false)
	const [totalPFR, setTotalPFR] = useState<number>(0.00);
	const dispatch = useDispatch<AppDispatch>();
	const PFRName = "Total Estimated PFR";
	const newPFRName = SummarySelections.NONADJUSTED;

  const isDisabledAfterConvert = useIsDisabledAfterConvert()

	useEffect(() => {
    const financialSummary = estimate?.nonAdjustedSummary

    if (financialSummary && financialSummary?.totalPFR !== totalPFR) {
      setTotalPFR(financialSummary?.totalPFR!);
    }
    if (estimate) {                 
      const crossoverPFRToShow = estimate.nonAdjustedSummary?.totalPFR;
      setTotalPFR(crossoverPFRToShow!)
    }
  }, [estimate]);

	const handleTotalEstimatedPfrSelected = () => {
    setTotalEstimatedPfrSelected(!totalEstimatedPfrSelected);

    if (!estimate) {
      dispatch(showErrorStatus('Missing estimate data'));
      return;
    }

    if (estimate.summarySelection !== newPFRName) {
        dispatch(
            saveSelectedEstimate({
                estimate: {
                    ..._.omit(estimate, [
                        'estimateId',
                        'createUserId',
                        'createDt',
                        'lastUpdateUserId',
                        'lastUpdateDt',
                        'summary',
                        'crossOverSummary',
                        'priorCareSummary',
                    ]),
                    summarySelection: newPFRName,
                    patientId: estimate.patientId,
                    ...(rateAsPayorPlan && { rateAsPayorPlan }),
                },
                estimateId: estimate.estimateId,
            })
        );
    }
  }

  const sideBarColor = estimate?.summarySelection === newPFRName ? "#6EACDE" : "#999999";

	const handleFinSumModalCancel = () => {
    setFinancialSummaryModalOpen(false);
  };

	const openFinancialSummaryModal = () => {
    // if new estimate and crossover is true..  
    if (estimate && estimate.isPlanYearCrossover) {
      setIsCrossoverSummaryModalOpen(true)
    } else {
      setFinancialSummaryModalOpen(true);
    }
	}

  const summaryExists = estimate?.facilityLevelOfCare && estimate?.facilityLevelOfCare?.length > 0 && estimate?.summary

	return (
        <>
            {summaryExists && (
                <>
                    <Box
                        border={1}
                        borderColor={sideBarColor}
                        style={{
                            backgroundColor: 'white',
                            marginBottom: '1.5em',
                        }}
                    >
                        <Grid container direction="row">
                            <Grid item direction="column" md={12}></Grid>
                        </Grid>
                        <Grid container direction="row">
                            <Grid
                                item
                                direction="column"
                                md={12}
                                xs={12}
                                style={{
                                    color: 'white',
                                    backgroundColor: sideBarColor,
                                    padding: '0.25em',
                                }}
                            >
                                Patient Financial Responsibility
                                {(
                                    estimate?.isPlanYearCrossover 
                                      ? estimate?.crossoverSelection === CrossoverSelectionEnum.BEFORE
                                          ? ' before Crossover'
                                          : ' with Crossover'
                                      : ''
                                )}
                            </Grid>
                            <Grid
                                item
                                direction="column"
                                md={12}
                                style={{
                                    padding: '1em',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{float: 'left'}}
                                >
                                    Total Estimated PFR
                                </Typography>

                                <FormControlLabel
                                    checked={
                                          estimate?.summarySelection! ===
                                            newPFRName
                                    }
                                    control={<Checkbox color="primary" />}
                                    id="totalPFR"
                                    name="totalPFR"
                                    label={
                                        <strong test-id="sidebar-estimate-pfr-value">
                                            {formatNumberToUSD(totalPFR)}
                                        </strong>
                                    }
                                    labelPlacement="start"
                                    style={{float: 'right'}}
                                    onChange={() => {
                                        handleTotalEstimatedPfrSelected();
                                    }}
                                    disabled={isDisabledAfterConvert}
                                />
                            </Grid>
                        </Grid>
                        <Grid container direction="row">
                            <Grid
                                item
                                direction="column"
                                md={12}
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                <IconButton
                                    color="primary"
                                    title="Open Details"
                                    style={{fontSize: '1.5rem'}}
                                    onClick={openFinancialSummaryModal}
                                    test-id="sidebar-estimate-pfr-open-details"
                                >
                                    <OpenInNewIcon fontSize="small" /> Open
                                    Details
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Box>
                    {isCrossoverSummaryModalOpen && (
                        <CrossoverSummaryModal open={true} estimate={estimate!} handleCancel={() => {setIsCrossoverSummaryModalOpen(false)}}/>
                    )}
                    {financialSummaryModalOpen && (
                        <EstFinancialSummaryModal
                            open={financialSummaryModalOpen}
                            handleFinancialSummaryModalCancel={
                                handleFinSumModalCancel
                            }
                        />
                    )}
                </>
            )}
        </>
    );
} 